import React, { useState, useEffect, useRef } from 'react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const POLL_INTERVAL = 8000;

async function chatFetch(path, options = {}) {
  const r = await fetch(BACKEND + '/api' + path, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });
  if (!r.ok) throw new Error('Request failed');
  return r.json();
}

export default function ChatWidget() {
  const [open, setOpen]         = useState(false);
  const [available, setAvail]   = useState(false);
  const [messages, setMessages] = useState([]);
  const [text, setText]         = useState('');
  const [name, setName]         = useState('');
  const [sending, setSending]   = useState(false);
  const [sent, setSent]         = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    const poll = async () => {
      try {
        const d = await chatFetch('/chat/status');
        setAvail(d.available);
        if (open) {
          const msgs = await chatFetch('/chat/messages/public');
          setMessages(Array.isArray(msgs) ? msgs : []);
        }
      } catch (_) {}
    };
    poll();
    const t = setInterval(poll, POLL_INTERVAL);
    return () => clearInterval(t);
  }, [open]);

  useEffect(() => {
    if (open && bottomRef.current)
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      await chatFetch('/chat/message', {
        method: 'POST',
        body: JSON.stringify({ name: name || 'Anonymous', message: text }),
      });
      setMessages(prev => [...prev, {
        sender_name: name || 'Anonymous',
        message: text,
        created_at: new Date().toISOString(),
        is_admin: false,
      }]);
      setText('');
      setSent(true);
      setTimeout(() => setSent(false), 3000);
    } catch (e) {
      console.error('Chat send failed:', e);
    }
    setSending(false);
  };

  const dot = available
    ? { background: '#22c55e', boxShadow: '0 0 6px #22c55e' }
    : { background: '#ef4444' };

  const s = {
    wrap:    { position: 'fixed', bottom: 24, right: 24, zIndex: 9999, fontFamily: 'sans-serif' },
    btn:     { width: 52, height: 52, borderRadius: '50%', background: '#1d4ed8', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', position: 'relative' },
    indic:   { position: 'absolute', top: 2, right: 2, width: 12, height: 12, borderRadius: '50%', border: '2px solid white', ...dot },
    panel:   { position: 'absolute', bottom: 64, right: 0, width: 320, background: '#0f172a', border: '1px solid #1e293b', borderRadius: 12, boxShadow: '0 8px 30px rgba(0,0,0,0.5)', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
    header:  { background: '#1d4ed8', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    title:   { color: '#fff', fontWeight: 600, fontSize: 14, margin: 0 },
    sub:     { color: 'rgba(255,255,255,0.75)', fontSize: 11, marginTop: 1 },
    close:   { background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: '0 4px' },
    status:  { padding: '6px 12px', fontSize: 11, background: available ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)', color: available ? '#86efac' : '#fca5a5', textAlign: 'center' },
    msgs:    { flex: 1, overflowY: 'auto', padding: '12px 12px 4px', maxHeight: 220, minHeight: 80 },
    empty:   { color: '#64748b', fontSize: 13, textAlign: 'center', padding: '20px 0' },
    sender:  { fontSize: 10, color: 'rgba(255,255,255,0.5)', marginBottom: 2 },
    footer:  { padding: '10px 12px', borderTop: '1px solid #1e293b' },
    nameInp: { width: '100%', background: '#1e293b', border: '1px solid #334155', borderRadius: 6, padding: '6px 8px', color: '#e2e8f0', fontSize: 12, marginBottom: 6, boxSizing: 'border-box', outline: 'none' },
    row:     { display: 'flex', gap: 6, alignItems: 'center' },
    inp:     { flex: 1, background: '#1e293b', border: '1px solid #334155', borderRadius: 6, padding: '8px 10px', color: '#e2e8f0', fontSize: 13, outline: 'none', fontFamily: 'inherit' },
    send:    { background: '#1d4ed8', border: 'none', borderRadius: 6, width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', cursor: 'pointer', fontSize: 16, flexShrink: 0 },
    sentTxt: { color: '#22c55e', fontSize: 11, marginTop: 4, textAlign: 'center' },
  };

  const bubble = (admin) => ({
    marginBottom: 8, padding: '8px 10px', borderRadius: 8, fontSize: 13,
    background: admin ? '#1e293b' : '#1d4ed8', color: '#e2e8f0',
    maxWidth: '80%',
  });

  return (
    <div style={s.wrap}>
      {open && (
        <div style={s.panel}>
          <div style={s.header}>
            <div>
              <div style={s.title}>Contact Support</div>
              <div style={s.sub}>We typically reply quickly</div>
            </div>
            <button style={s.close} onClick={() => setOpen(false)}>×</button>
          </div>
          <div style={s.status}>
            <span style={{ ...dot, display:'inline-block', width:7, height:7, borderRadius:'50%', marginRight:5, verticalAlign:'middle' }} />
            {available ? 'Support is online' : 'Support is offline — leave a message'}
          </div>
          <div style={s.msgs}>
            {messages.length === 0
              ? <div style={s.empty}>No messages yet. Say hello! 👋</div>
              : messages.map((m, i) => (
                  <div key={i} style={{ display:'flex', flexDirection:'column', alignItems: m.is_admin ? 'flex-start' : 'flex-end' }}>
                    <div style={s.sender}>{m.sender_name}</div>
                    <div style={bubble(m.is_admin)}>{m.message}</div>
                  </div>
                ))
            }
            <div ref={bottomRef} />
          </div>
          <div style={s.footer}>
            <input
              style={s.nameInp}
              placeholder="Your name (optional)"
              value={name}
              onChange={e => setName(e.target.value)}
            />
            <div style={s.row}>
              <input
                style={s.inp}
                placeholder="Type a message..."
                value={text}
                onChange={e => setText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              />
              <button
                style={{ ...s.send, opacity: sending || !text.trim() ? 0.5 : 1, cursor: sending || !text.trim() ? 'not-allowed' : 'pointer' }}
                onClick={send}
                disabled={sending || !text.trim()}
              >
                ➤
              </button>
            </div>
            {sent && <div style={s.sentTxt}>✓ Message sent!</div>}
          </div>
        </div>
      )}
      <button style={s.btn} onClick={() => setOpen(o => !o)} title="Contact Support">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
        <div style={s.indic} />
      </button>
    </div>
  );
}
